/**
 * The summary rail — `docs/proposal.md` §6 item 11.
 *
 * 344px, sticky, from `RAIL_FROM` (1280px) up. It is the answer to the only
 * question somebody scrolling a four-screen document actually holds in their
 * head: what am I buying and what does it come to. Below it the same two lines
 * are the phone bottom bar (`sticky-bar.tsx`), which is why the total and the
 * **Review and sign** button are built from the same numbers in both.
 *
 * **One row per included visit**, not per service and not per line: the rail
 * is a receipt of the choices, and a receipt that lists forty lines is the
 * document again rather than a summary of it. Its figures come from
 * `selectionTotals().byVisit`, so a tick anywhere on the page moves them and
 * nothing here re-prices anything.
 *
 * The monthly line under the total ("Or $288 / month for 3 months, plus
 * $2,663.72 for the one-time work") is `MonthSentence` in `month-view.tsx`,
 * beside the bar's `monthLine()` there, because two copies of one sentence
 * are two sentences the moment one is edited.
 *
 * **It follows the total card's toggle** (Aaron's review, item 2.5). On
 * **Per month** — which is where a quote priced by its billing plan opens —
 * the rail leads with "Monthly $950 / month" and says the total under it
 * ("For 7 months. $6,650.44 in all."); on **Total** it leads with the total
 * and says the month under it. Whichever leads, the figures are the same
 * `monthSplit()`, so the two readings cannot disagree about the money.
 */
import type {
  MonthSplit, MonthView, ProposalPayment, ProposalView, ProposalVisit, SelectionTotals,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollars, formatDollarsExact } from '../../lib/format.js'
import { GlyphCard, GlyphChat, GlyphChevronRight, GlyphPhone } from './glyphs.js'
import { MonthSentence, TotalSentence, leadLabel } from './month-view.js'

export interface RailProps {
  view: ProposalView
  totals: SelectionTotals
  /** False while a choose-one group has no answer: the total is a dash. */
  ready: boolean
  /** True while `payments` still describe the selection on screen. */
  priced: boolean
  /** The rows the page is holding — live once the `price` call has answered. */
  payments: ProposalPayment[]
  /** The same total by the period, or null where there is nothing to say (§4.4). */
  month?: { cadence: MonthView; split: MonthSplit } | null
  /**
   * Lead with the month: the reader is on **Per month** and it is offered
   * (`monthOffered`). The page decides, because the bar and the total card
   * must lead the same way.
   */
  byMonth?: boolean
  /** Opens "Ask a question" where the page can; the phone number otherwise. */
  onAsk?: () => void
  onSign: () => void
  /** Preview and signed states have no signature to scroll to. */
  signLabel?: string
}

export function ProposalRail(props: RailProps) {
  const {
    view, totals, ready, priced, payments, month, onSign, onAsk, signLabel = 'Review and sign',
  } = props
  const deposit = priced ? depositOf(payments) : null
  const lead = props.byMonth && ready && month && month.split.rows.length > 0 ? month : null

  const rows = view.visits
    .filter((visit) => totals.byVisit[visit.id] !== undefined)
    .map((visit) => ({
      id: visit.id,
      /* An answer says which question it answers: "Renovation · Premium". */
      name: visit.kind === 'choice' ? answerName(view, visit) : visit.name,
      count: countWords(visit),
      amount: totals.byVisit[visit.id] ?? 0,
      openEnded: visit.count.openEnded,
      recurring: visit.recurring,
    }))

  return (
    <aside className="flex w-[344px] shrink-0 flex-col gap-3.5">
      <section className="flex flex-col gap-1 rounded-[18px] border border-line bg-surface px-6 pb-[22px]
                          pt-6 shadow-proposal">
        <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-text-faint">
          Your selection
        </span>

        {rows.length > 0 && (
          <div className="mt-2 flex flex-col border-t border-line-soft pt-1">
            {rows.map((row) => (
              <div key={row.id}
                className="flex items-start justify-between gap-2.5 py-[9px] text-[14px]">
                <span className="flex min-w-0 flex-col">
                  <span className="font-semibold text-text">{row.name}</span>
                  <span className="tnum text-[12.5px] text-text-faint">{row.count}</span>
                </span>
                <span className="tnum shrink-0 text-[14px] font-semibold text-text">
                  {row.openEnded && row.recurring
                    ? `${formatDollarsExact(row.recurring.amount)} / ${row.recurring.label}`
                    : formatDollarsExact(row.amount)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/*
          * No rate means no tax line (§1.4). A "$0" estimate beside a
          * signature is a figure somebody acts on; a missing one is a
          * question they ask, and the question has an answer.
          */}
        {ready && view.tax.rate > 0 && (
          <div className="flex justify-between border-t border-line-soft pt-2 text-[13.5px]
                          text-text-muted">
            <span>Sales tax (estimated)</span>
            <span className="tnum font-semibold">{formatDollarsExact(totals.tax)}</span>
          </div>
        )}

        {lead
          ? (
            <>
              {/*
                * The month, said the way board 12 says a price — the figure
                * and its unit set apart — then the total under it. One price
                * said two ways: nothing here is a second figure.
                */}
              <div className="flex items-baseline justify-between gap-3 pb-1.5 pt-2.5">
                <b className="text-[15px] font-bold text-text">
                  {leadLabel(lead.cadence, lead.split)}
                </b>
                <span className="flex items-baseline gap-1 whitespace-nowrap">
                  <b className="tnum text-[30px] font-bold tracking-[-0.025em] text-text">
                    {formatDollars(lead.split.perPeriod)}
                  </b>
                  <span className="text-[14px] font-semibold text-text-body">
                    {`/ ${lead.cadence.label}`}
                  </span>
                </span>
              </div>
              <p className="tnum m-0 pb-3.5 text-[12.5px] leading-[1.45] text-text-muted">
                <TotalSentence month={lead.cadence} split={lead.split} />
              </p>
            </>
            )
          : (
            <>
              <div className="flex items-baseline justify-between pb-1.5 pt-2.5">
                <b className="text-[15px] font-bold text-text">Total</b>
                <b className="tnum text-[30px] font-bold tracking-[-0.025em] text-text">
                  {ready ? formatDollarsExact(totals.total) : '—'}
                </b>
              </div>
              {!ready && (
                <p className="m-0 pb-3.5 text-[12.5px] leading-[1.45] text-text-muted">
                  Choose an option on the left to see your total.
                </p>
              )}
              {/*
                * The second line. One price said two ways: the figure above
                * never moves between the two readings.
                */}
              {ready && month && month.split.rows.length > 0 && (
                <p className="tnum m-0 pb-3.5 text-[12.5px] leading-[1.45] text-text-muted">
                  <MonthSentence month={month.cadence} split={month.split} />
                </p>
              )}
            </>
            )}

        {deposit && (
          <div className="flex items-center gap-2.5 rounded-[12px] border border-brand-ring
                          bg-brand-t1 px-3 py-[11px] text-[13.5px] leading-[1.35]">
            <GlyphCard size={18} className="shrink-0 text-brand-tx" />
            <span className="flex-1">
              Due when you accept<br />
              <b className="tnum text-[16px]">{formatDollarsExact(deposit)}</b>{' '}
              <span className="text-text-muted">deposit</span>
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={onSign}
          className="mt-3.5 flex h-[54px] w-full items-center justify-center gap-2
                     rounded-[14px] bg-brand text-[16px] font-semibold text-brand-on"
        >
          {signLabel}
          <GlyphChevronRight size={18} />
        </button>
      </section>

      {view.estimator && (
        <section className="flex items-center gap-3.5 rounded-[18px] border border-line
                            bg-surface px-5 py-4 shadow-proposal">
          <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center
                           rounded-full bg-brand-t2 font-bold text-brand-tx">
            {view.estimator.initials}
          </span>
          <span className="flex min-w-0 flex-1 flex-col leading-[1.35]">
            <b className="truncate text-[14.5px]">
              {`Questions? Ask ${firstName(view.estimator.name)}`}
            </b>
            {view.estimator.phone && (
              <span className="truncate text-[13px] text-text-muted">{view.estimator.phone}</span>
            )}
          </span>
          {/*
            * Board 1 draws a speech bubble: the card's first way in is a
            * question on the page, and the number above it is for a call.
            */}
          {onAsk
            ? (
              <button type="button" onClick={onAsk}
                aria-label={`Ask ${view.estimator.name} a question`}
                className="-m-3 flex h-11 w-11 shrink-0 items-center justify-center text-brand-tx">
                <GlyphChat size={20} />
              </button>
              )
            : view.estimator.phone && (
              <a
                href={`tel:${view.estimator.phone}`}
                aria-label={`Call ${view.estimator.name}`}
                className={cn('-m-3 flex h-11 w-11 shrink-0 items-center justify-center text-brand-tx')}
              >
                <GlyphPhone size={20} />
              </a>
            )}
        </section>
      )}
    </aside>
  )
}

/**
 * "Due when you accept" — the amount, when there is one.
 *
 * It reads the payment rows rather than the plans, because a deposit is a
 * *row* ("When you accept"): a plan that happens to open with one and a
 * deposit plan are the same thing to somebody reading this, and asking the
 * rows means one answer instead of two.
 */
export function depositOf(payments: ProposalPayment[]): number | null {
  const row = payments.find((one) => one.whenLabel === 'When you accept' && one.amount > 0)
  return row ? row.amount : null
}

/** "9 visits", "3 days", "Ongoing". */
export function countWords(visit: ProposalView['visits'][number]): string {
  if (visit.count.openEnded) return 'Ongoing'
  if (visit.count.n === null) return ''
  return `${visit.count.n} ${visit.count.unit}`
}

const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name

/** "Front bed renovation · Premium" for a visit that answers a question. */
function answerName(view: ProposalView, visit: ProposalVisit): string {
  const group = view.groups.find((one) => one.id === visit.groupId)
  const option = group?.options.find((one) => one.visitIds.includes(visit.id))
  if (!group || !option) return visit.name
  return option.visitIds.length === 1 ? `${group.name} · ${option.name}` : visit.name
}
